import React, { useMemo } from 'react';
import DetailsTab from 'v2/pages/DataVendorDetails/DetailsTab/DetailsTab';
import ContactTab from 'v2/pages/DataVendorDetails/ContactTab/ContactTab';
import ActivityTab from 'v2/common/DRM/ActivityTab/ActivityTab';
import FilesTab from 'v2/common/DRM/FilesTab/FilesTab';
import CustomTabComponent from 'v2/common/DRM/CustomTab';
import {
  ActivityAssociation,
  DataVendor,
  DrmPage,
  DjangoListResponse,
  DataVendorDetailsTabType,
  CustomTab,
  DataProduct,
  SearchQueryResponse,
} from 'v2/models';
import useGetContacts from 'v2/hooks/useGetContacts';
import { mapContactAssociations } from 'v2/utils/activityUtils';
import { useQuery } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import { fetchSearches } from 'v2/hooks/useFetchSearchesPaginated';

interface DataVendorTabContentProps {
  dataVendor: DataVendor;
  selectedTab: string;
  customTabs: DjangoListResponse<CustomTab> | undefined;
  TABS: DataVendorDetailsTabType[];
  refetch?: () => void;
}

const DataVendorTabContent = ({
  selectedTab,
  dataVendor,
  customTabs,
  TABS,
  refetch,
}: DataVendorTabContentProps) => {
  const { data: dataVendorContacts } = useGetContacts(
    {
      data_vendor: dataVendor.id,
    },
    {},
    0
  );

  const { data: dataProducts } = useQuery<DjangoListResponse<DataProduct>>(
    ['datavendordetails_dataproducts', dataVendor.id],
    async () =>
      await restFactory.get(
        `/dj/api/v1/data_vendor/${dataVendor.id}/data_products/`
      ),
    { cacheTime: 0, refetchOnWindowFocus: false }
  );

  const { data: requests = null } = useQuery<SearchQueryResponse | null>(
    'requests',
    async () => {
      if (!dataVendor.data_provider) {
        return null;
      }
      return await fetchSearches({
        depth: 'master-organization',
        data_provider: dataVendor.data_provider,
        data_provider_response: 'affirmative',
      });
    },
    { cacheTime: 0, refetchOnWindowFocus: false }
  );

  const contactAssociations = useMemo<ActivityAssociation[]>(() => {
    return mapContactAssociations(dataVendorContacts);
  }, [dataVendorContacts]);

  const dataProductAssociations = useMemo<ActivityAssociation[]>(
    () =>
      (dataProducts?.results || []).map((dataProduct) => ({
        table_name: 'DataProduct',
        name: dataProduct.name,
        object_id: dataProduct.id,
        selected: false,
        editable: true,
      })),
    [dataProducts]
  );

  const requestsAssociations = useMemo<ActivityAssociation[]>(
    () =>
      (requests?.results || []).map((request) => ({
        table_name: 'Searches',
        name: request.name,
        object_id: request.id,
        selected: false,
        editable: true,
      })),
    [requests]
  );

  const dataVendorAssociations: ActivityAssociation[] = [
    {
      table_name: 'DataVendor',
      name: dataVendor.name,
      object_id: dataVendor.id,
      is_mandatory: true,
    },
  ];

  const defaultAssociations = [
    ...dataVendorAssociations,
    ...contactAssociations,
    ...dataProductAssociations,
    ...requestsAssociations,
  ];

  const renderTab = () => {
    switch (selectedTab) {
      case 'details':
        return (
          <DetailsTab dataVendor={dataVendor} onChange={() => refetch?.()} />
        );
      case 'contacts':
        return <ContactTab dataVendor={dataVendor} />;
      case 'activity':
        return (
          <ActivityTab
            relatedTable="DataVendor"
            relatedObjectId={dataVendor.id}
            defaultAssociations={defaultAssociations}
          />
        );
      case 'files':
        return (
          <FilesTab
            relatedTable="DataVendor"
            relatedObjectId={dataVendor.id}
            dataVendorId={dataVendor.id}
            primaryObjectType="DataVendor"
            defaultAssociations={defaultAssociations}
          />
        );
      default:
        if (!TABS.some((tab) => tab['eventKey'] === selectedTab)) {
          return (
            <CustomTabComponent
              drmPage={DrmPage.DATA_VENDOR}
              tab={customTabs?.results.find((tab) => tab.name === selectedTab)}
              entity={dataVendor}
              onUpdate={(_) => refetch?.()}
            />
          );
        }
        return null;
    }
  };

  return <div>{renderTab()}</div>;
};

export default DataVendorTabContent;
